/**
 * Signup Routes Configuration
 *
 * This file contains all signup-related route definitions extracted from the main router.
 * It provides better organization and maintainability for signup-specific routing logic.
 */

import type { RouteRecordRaw } from 'vue-router'
import { loadLocales } from '@/utils/i18n.utils'
import { useSignupStore } from '@/stores/SignupStore'
import {
  SIGNUP_ROUTE_NAME,
  SIGNUP_INFO_ROUTE_NAME,
  SIGNUP_SECURITY_ROUTE_NAME,
  SIGNUP_MATCH_ROUTE_NAME,
  SIGNUP_MATCH_INFO_ROUTE_NAME,
  SIGNUP_INFO_EDIT_ROUTE_NAME,
  SIGNUP_COPARENT_INFO_EDIT_ROUTE_NAME,
  SIGNUP_CHILD_INFO_EDIT_ROUTE_NAME,
  SIGNUP_PASSWORD_EDIT_ROUTE_NAME,
  SIGNUP_PHONE_EDIT_ROUTE_NAME
} from './signup.constants'

// Signup component imports
const SignupLayout = () => import('@/layouts/SignupLayout.vue')
const SignupEmail = () => import('@/pages/signup/SignupEmail.vue')
const SignupInfo = () => import('@/pages/signup/SignupInfo.vue')
const SignupSecurity = () => import('@/pages/signup/SignupSecurity.vue')
const SignupMatchInfo = () => import('@/pages/signup/SignupMatchInfo.vue')
const SignupMatch = () => import('@/pages/signup/SignupMatch.vue')
const SignupInfoEdit = () => import('@/pages/signup/SignupInfoEdit.vue')
const SignupCoparentInfoEdit = () =>
  import('@/pages/signup/SignupCoparentInfoEdit.vue')
const SignupChildInfoEdit = () =>
  import('@/pages/signup/SignupChildInfoEdit.vue')
const SignupPasswordEdit = () => import('@/pages/signup/SignupPasswordEdit.vue')
const SignupPhoneEdit = () => import('@/pages/signup/SignupPhoneEdit.vue')

/**
 * Get signup route configuration
 */
export function getSignupRoutes(): RouteRecordRaw {
  return {
    path: '/signup',
    component: SignupLayout,
    beforeEnter: async () => await loadLocales('accountSettings', 'signup'),
    children: [
      { name: SIGNUP_ROUTE_NAME, path: '/signup', component: SignupEmail },
      {
        name: SIGNUP_INFO_ROUTE_NAME,
        path: '/info',
        component: SignupInfo,
        beforeEnter: () => {
          const signupStore = useSignupStore()
          if (
            signupStore.$state.signupEmail &&
            signupStore.$state.signupGuid &&
            signupStore.$state.verifyCode
          ) {
            return true
          } else {
            return SIGNUP_ROUTE_NAME
          }
        }
      },
      {
        name: SIGNUP_SECURITY_ROUTE_NAME,
        path: '/security',
        component: SignupSecurity,
        meta: { requiresAuth: true, requiresMatched: false }
      },
      {
        name: SIGNUP_MATCH_INFO_ROUTE_NAME,
        path: '/matchInfo',
        component: SignupMatchInfo,
        meta: { requiresAuth: true, requiresMatched: false }
      },
      {
        name: SIGNUP_MATCH_ROUTE_NAME,
        path: '/match',
        component: SignupMatch,
        meta: { requiresAuth: true, requiresMatched: false }
      },
      {
        name: SIGNUP_INFO_EDIT_ROUTE_NAME,
        path: '/infoEdit',
        component: SignupInfoEdit,
        meta: { requiresAuth: true }
      },
      {
        name: SIGNUP_COPARENT_INFO_EDIT_ROUTE_NAME,
        path: '/coparentInfoEdit',
        component: SignupCoparentInfoEdit,
        meta: { requiresAuth: true }
      },
      {
        name: SIGNUP_CHILD_INFO_EDIT_ROUTE_NAME,
        path: '/childInfoEdit',
        component: SignupChildInfoEdit,
        meta: { requiresAuth: true }
      },
      {
        name: SIGNUP_PASSWORD_EDIT_ROUTE_NAME,
        path: '/passwordEdit',
        component: SignupPasswordEdit,
        meta: { requiresAuth: true }
      },
      {
        name: SIGNUP_PHONE_EDIT_ROUTE_NAME,
        path: '/phoneEdit',
        component: SignupPhoneEdit,
        meta: { requiresAuth: true }
      }
    ]
  }
}
